import axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import Config from 'react-native-config';
import { API_ENDPOINTS } from './apiEndpoints';
import { normalizeApiError } from './apiError';
import {
  getKioskAccessToken,
  getKioskDeviceIdentifier,
} from '../services/kioskTokenStore';
import { getActiveRoleCode } from '../services/activeRoleStore';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  saveTokens,
} from '../services/secureTokenService';

type RequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  metadata?: { startedAt: number };
};

type RefreshBody = {
  data?: { accessToken?: unknown; refreshToken?: unknown };
  code?: string;
};

const timeout = Number(Config.API_REQUEST_TIMEOUT_MS) || 30000;
const clientConfig = {
  baseURL: Config.API_BASE_URL,
  timeout,
  withCredentials: true,
  headers: { Accept: 'application/json' },
};

export const apiClient = axios.create(clientConfig);
export const authApiClient = axios.create(clientConfig);

const loggingEnabled = __DEV__ && Config.ENABLE_API_LOGGING === 'true';
const noAuthorization = [
  API_ENDPOINTS.AUTH.LOGIN,
  API_ENDPOINTS.AUTH.REFRESH,
  API_ENDPOINTS.KIOSKS.ACTIVATE,
  API_ENDPOINTS.KIOSKS.REFRESH,
  API_ENDPOINTS.KIOSKS.ACTIVATION_STATUS,
];
const noRefresh = [
  API_ENDPOINTS.AUTH.LOGIN,
  API_ENDPOINTS.AUTH.REFRESH,
  API_ENDPOINTS.AUTH.LOGOUT,
  API_ENDPOINTS.KIOSKS.ACTIVATE,
  API_ENDPOINTS.KIOSKS.REFRESH,
  API_ENDPOINTS.KIOSKS.ACTIVATION_STATUS,
];
const ineligibleCodes = new Set([
  'ACCOUNT_NOT_AVAILABLE',
  'INVALID_CREDENTIALS',
  'ACCESS_DENIED',
  'CORS_NOT_ALLOWED',
]);
const kioskFatalCodes = new Set([
  'KIOSK_INACTIVE',
  'KIOSK_UNAUTHORIZED',
  'KIOSK_DEVICE_MISMATCH',
  'KIOSK_ORGANIZATION_MISMATCH',
  'INVALID_REFRESH_TOKEN',
  'INVALID_ACTIVATION_TOKEN',
]);

let refreshPromise: Promise<string> | null = null;
let sessionInvalidated: ((message: string) => void) | null = null;

export function registerSessionInvalidationHandler(
  handler: ((message: string) => void) | null,
) {
  sessionInvalidated = handler;
}

function endpointMatches(url: string | undefined, endpoints: readonly string[]) {
  return Boolean(url && endpoints.some(endpoint => url.endsWith(endpoint)));
}

function requireToken(value: unknown, label: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`Refresh response did not contain ${label}`);
  }
  return value;
}

async function performRefresh(): Promise<string> {
  const refreshToken = await getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await authApiClient.post<RefreshBody>(
    API_ENDPOINTS.AUTH.REFRESH,
    { refreshToken },
  );
  const accessToken = requireToken(response.data?.data?.accessToken, 'an access token');
  const nextRefreshToken = requireToken(
    response.data?.data?.refreshToken,
    'a refresh token',
  );
  await saveTokens({ accessToken, refreshToken: nextRefreshToken });
  return accessToken;
}

async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function attachActiveRole(config: InternalAxiosRequestConfig) {
  const roleCode = await getActiveRoleCode();
  if (roleCode) config.headers['X-Active-Role'] = roleCode;
  return config;
}

authApiClient.interceptors.request.use(attachActiveRole);

apiClient.interceptors.request.use(async config => {
  const request = config as RequestConfig;
  request.metadata = { startedAt: Date.now() };
  if (!endpointMatches(request.url, noAuthorization)) {
    const kioskToken = getKioskAccessToken();
    if (kioskToken) {
      request.headers.Authorization = `Bearer ${kioskToken}`;
      const deviceId = getKioskDeviceIdentifier();
      if (deviceId) {
        request.headers['x-device-id'] = deviceId;
      }
    } else {
      const accessToken = await getAccessToken();
      if (accessToken) request.headers.Authorization = `Bearer ${accessToken}`;
      await attachActiveRole(request);
    }
  }
  return request;
});

apiClient.interceptors.response.use(
  response => {
    if (loggingEnabled) {
      const startedAt =
        (response.config as RequestConfig).metadata?.startedAt ?? Date.now();
      console.info('[API]', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        duration: Date.now() - startedAt,
        requestId: response.headers['x-request-id'] ?? null,
      });
    }
    return response;
  },
  async (error: AxiosError<{ code?: string; message?: string }>) => {
    const request = error.config as RequestConfig | undefined;
    const code = error.response?.data?.code;

    if (loggingEnabled) {
      const startedAt = request?.metadata?.startedAt ?? Date.now();
      console.info('[API]', {
        method: request?.method?.toUpperCase(),
        url: request?.url,
        status: error.response?.status ?? null,
        duration: Date.now() - startedAt,
        requestId: error.response?.headers?.['x-request-id'] ?? null,
        code: error.response?.data?.code ?? null,
        message: error.response?.data?.message ?? null,
      });
    }

    const kioskActive = Boolean(getKioskAccessToken());
    if (kioskActive) {
      if (kioskFatalCodes.has(code ?? '')) {
        const { invalidateKioskSession } = await import(
          '../services/kioskAuthService'
        );
        await invalidateKioskSession(
          code === 'KIOSK_DEVICE_MISMATCH'
            ? 'This device does not match the registered kiosk. Activate again on the correct device.'
            : code === 'KIOSK_INACTIVE'
              ? 'This kiosk has been deactivated. Contact an administrator.'
              : 'This kiosk is no longer authorized. Activate it again.',
        );
        return Promise.reject(normalizeApiError(error));
      }

      const kioskRefreshEligible =
        error.response?.status === 401 &&
        request &&
        !request._retry &&
        !endpointMatches(request.url, noRefresh) &&
        !ineligibleCodes.has(code ?? '');

      if (kioskRefreshEligible) {
        request._retry = true;
        try {
          const { refreshKioskAccessToken } = await import(
            '../services/kioskAuthService'
          );
          const accessToken = await refreshKioskAccessToken();
          request.headers.Authorization = `Bearer ${accessToken}`;
          const deviceId = getKioskDeviceIdentifier();
          if (deviceId) request.headers['x-device-id'] = deviceId;
          return apiClient(request);
        } catch (refreshError) {
          return Promise.reject(normalizeApiError(refreshError));
        }
      }

      return Promise.reject(normalizeApiError(error));
    }

    if (code === 'INVALID_SESSION' || code === 'ACCOUNT_NOT_AVAILABLE') {
      await clearTokens();
      sessionInvalidated?.(
        'Your session is no longer available. Please sign in again.',
      );
      return Promise.reject(normalizeApiError(error));
    }

    const eligible =
      error.response?.status === 401 &&
      request &&
      !request._retry &&
      !endpointMatches(request.url, noRefresh) &&
      !ineligibleCodes.has(code ?? '');

    if (!eligible) return Promise.reject(normalizeApiError(error));

    request._retry = true;
    try {
      const accessToken = await refreshAccessToken();
      request.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(request);
    } catch (refreshError) {
      await clearTokens();
      sessionInvalidated?.(
        'Your session is no longer available. Please sign in again.',
      );
      return Promise.reject(normalizeApiError(refreshError));
    }
  },
);
